import { CalendarCheck, CalendarClock, Clock, Truck, Hammer, CheckCircle, Play, SkipForward, XCircle } from 'lucide-vue-next'
import type { Component } from 'vue'
import type { components, paths } from '@/api/schema'
import type { Resource } from '@/api/resources'
import { isOverdue } from '@/lib/dates'
import { CHIP, CHIP_BUTTON, type MarkTone } from '@/lib/statePalette'

export type VisitStatus = NonNullable<components['schemas']['VisitResource']['attributes']['status']>

// The FSM event, read straight off the generated schema — NOT imported from
// useVisits: a lib must not depend on a composable, and the schema is the same
// authority both of them read.
export type VisitEventName = NonNullable<
  NonNullable<
    paths['/api/visits/{id}']['patch']['requestBody']['content']['application/vnd.api+json']['data']['attributes']
  >['event']
>

// Shared status groupings for dashboards/KPIs (Console + Field). Reference these
// instead of inlining status arrays in a view.
export const ACTIVE_VISIT_STATUSES: VisitStatus[] = ['dispatched', 'working']
export const OPEN_VISIT_STATUSES: VisitStatus[] = ['pending', 'scheduled', 'dispatched', 'working']
export const TERMINAL_VISIT_STATUSES: VisitStatus[] = ['completed', 'skipped', 'cancelled']

// THE DAY METER's fill. ONE arithmetic for every surface that draws it, so the
// bars cannot disagree about the formula — each surface supplies its own count
// of what is « done with », because they hold different evidence.
//
// ⚠ NOT the stop's rank in the route. Rank looks like the same number and is
// not: it retreats FOR DOING THE WORK. Take a stop out of order and the hero
// jumps to a higher rank, then falls back to the earliest still-open one the
// moment it resolves. This meter can only move backwards when the DAY GROWS
// (a bank pull enlarges the denominator) — never as a consequence of finishing
// something, which is the property rank lacks and the reason it was rejected.
// Module-private: /plan was its only outside caller, and that screen is gone.
// The exported reading is dayProgressPercent, which owns the « done with »
// definition too — an exported bare ratio invites a second, disagreeing one.
function progressPercent(resolved: number, total: number): number {
  if (total <= 0) return 0
  return Math.round((resolved / total) * 100)
}

// The visit-status spelling of « done with », for a surface that has no better
// evidence than the statuses themselves (Home's day card).
//
// ⭐ Anything TERMINAL, not `completed` alone — so the bar's empty part is
// exactly the « N restantes » printed beside it, and a skipped stop stops
// leaving a sliver that can never close.
export function dayProgressPercent(visits: { attributes: { status?: string | null } }[]): number {
  const resolved = visits.filter(v => {
    const status = v.attributes.status
    return status != null && isVisitStatus(status) && TERMINAL_VISIT_STATUSES.includes(status)
  }).length
  return progressPercent(resolved, visits.length)
}

export interface VisitStateConfig {
  icon: Component
  badgeVariant: 'default' | 'secondary' | 'outline' | 'destructive'
  badgeClass: string
  buttonClass: string
  // The status ICON's colour on a stop card. Status is carried by an icon, never
  // by the card's background — the left band is the JOB TYPE's colour, always,
  // and a second (state) colour on the surface only fought it (user directive:
  // "left band = job colour, no background ever, icons for the states").
  iconClass: string
  // THE index-row mark's tone (StateMark — VisitStateTag variant="dot" and the
  // Travaux list's « Prochaine visite », design 169): pending is nothing yet,
  // a placed or running visit is in hand, completed is done, skipped and
  // cancelled recede.
  tone: MarkTone
}

const STATE_CONFIG: Record<VisitStatus, VisitStateConfig> = {
  // Pending is the most common and least interesting state — it whispers
  // (theme-safe outline), so the loud tags keep meaning something.
  pending:    { icon: CalendarClock, badgeVariant: 'outline',     badgeClass: 'bg-transparent text-muted-foreground border-border', buttonClass: CHIP_BUTTON.neutralDim, iconClass: 'text-muted-foreground', tone: 'neutral' },
  scheduled:  { icon: Clock,         badgeVariant: 'secondary',   badgeClass: CHIP.neutral, buttonClass: CHIP_BUTTON.neutral,      iconClass: 'text-muted-foreground', tone: 'teal' },
  dispatched: { icon: Truck,         badgeVariant: 'outline',     badgeClass: CHIP.blue,    buttonClass: CHIP_BUTTON.blue,         iconClass: 'text-blue-600 dark:text-blue-400', tone: 'teal' },
  working:    { icon: Hammer,        badgeVariant: 'default',     badgeClass: CHIP.green,   buttonClass: CHIP_BUTTON.green,        iconClass: 'text-green-700 dark:text-green-300', tone: 'teal' },
  completed:  { icon: CheckCircle,   badgeVariant: 'outline',     badgeClass: CHIP.emerald, buttonClass: CHIP_BUTTON.emerald,      iconClass: 'text-emerald-700 dark:text-emerald-300', tone: 'emerald' },
  skipped:    { icon: SkipForward,   badgeVariant: 'outline',     badgeClass: CHIP.amber,   buttonClass: CHIP_BUTTON.amber,        iconClass: 'text-amber-700 dark:text-amber-300', tone: 'dim' },
  cancelled:  { icon: XCircle,       badgeVariant: 'destructive', badgeClass: '',           buttonClass: '',                       iconClass: 'text-red-600 dark:text-red-400', tone: 'dim' },
}

const STATUS_SET = new Set<string>(Object.keys(STATE_CONFIG))
export function isVisitStatus(s: string): s is VisitStatus { return STATUS_SET.has(s) }

export function getVisitStateConfig(status: string | undefined): VisitStateConfig {
  return (status && isVisitStatus(status) ? STATE_CONFIG[status] : null) ?? STATE_CONFIG.pending
}

export function getVisitStateButtonClass(targetStatus: VisitStatus): string {
  return STATE_CONFIG[targetStatus].buttonClass
}

// Which statuses draw a state icon on a stop card: en route / live / done /
// skipped / cancelled — a state where something is happening or has happened.
// pending and scheduled stay icon-free (the time + job type already say it all);
// if every card carried a status icon, none would draw the eye.
const ICONED_STATUSES = new Set<string>([...ACTIVE_VISIT_STATUSES, ...TERMINAL_VISIT_STATUSES])
export function showsStatusIcon(status: string | undefined): boolean {
  return status != null && ICONED_STATUSES.has(status)
}

// Kind as STRUCTURE on the job pages (both surfaces): evaluations and work
// render as separate sections, so no row needs a kind badge. Sections only
// exist when an evaluation is present — the everyday all-work job keeps a
// flat, headerless list. One definition so console and handheld can't drift.
export function visitKindGroups(visits: Resource<'visit'>[]): Array<{ key: 'evaluation' | 'work'; labeled: boolean; items: Resource<'visit'>[] }> {
  const evaluations = visits.filter(v => v.attributes.kind === 'evaluation')
  if (!evaluations.length) return [{ key: 'work', labeled: false, items: visits }]
  const work = visits.filter(v => v.attributes.kind !== 'evaluation')
  const groups: Array<{ key: 'evaluation' | 'work'; labeled: boolean; items: Resource<'visit'>[] }> =
    [{ key: 'evaluation', labeled: true, items: evaluations }]
  if (work.length) groups.push({ key: 'work', labeled: true, items: work })
  return groups
}

// Row title: the name when there is one; otherwise an evaluation is called an
// evaluation and a work visit is named by its rank in the program.
// With an `ordinal` (from workOrdinals) it reads « Visite 2 de 3 » — the job
// fact. Without one, or for a lone treatment where « de 1 » says nothing, it
// falls back to the visit's own position.
export function visitTitle(
  visit: Resource<'visit'>,
  t: (key: string, params?: Record<string, unknown>) => string,
  ordinal?: { index: number; total: number } | null,
): string {
  const a = visit.attributes
  if (a.name) return a.name
  if (a.kind === 'evaluation') return t('visits.kind.evaluation')
  if (ordinal && ordinal.total > 1) return t('visits.visitOfTotal', { ...ordinal })
  return t('visits.visitN', { position: ordinal?.index ?? a.position })
}

// The "next visit" for a job: earliest non-terminal visit by scheduled_at
// (unscheduled/pending sort last), tie-broken by position. Jobs carry 1–3
// visits so this is a cheap client-side pick over the included set.
// The most recent COMPLETED look-see on a job — the ONE definition the invoice
// card reads on both surfaces, so a console and a handheld page cannot
// disagree about which visit the evaluation fee belongs to.
export function latestCompletedEvaluation(visits: Resource<'visit'>[]): Resource<'visit'> | null {
  for (let i = visits.length - 1; i >= 0; i--) {
    const v = visits[i]
    if (v.attributes.kind === 'evaluation' && v.attributes.status === 'completed') return v
  }
  return null
}

// Work that is still going to happen. Cancelled/skipped work is DEAD work —
// the one predicate behind `hasStandingWork` and `workOrdinals`, so "is there
// standing work?" and "how many treatments are there?" can never disagree.
export function isStandingWork(visit: Resource<'visit'>): boolean {
  const a = visit.attributes
  return a.kind !== 'evaluation' && a.status !== 'cancelled' && a.status !== 'skipped'
}

// A LIVE work commitment exists — the client mirror of Visit.standing_work
// (jobs filter[no_work_visits] is its negation). A job whose only work visit
// was cancelled has no standing work, so « Planifier les travaux » honestly
// reopens. Shared by both job pages' CTAs so the door can't appear under
// different conditions per surface.
export function hasStandingWork(visits: Resource<'visit'>[]): boolean {
  return visits.some(isStandingWork)
}

// « visite 2 de 3 » — the client mirror of Visit.work_ordinals_for: each work
// visit's 1-based rank among its job's LIVE work visits (by position) and the
// program total. Skipped/cancelled treatments leave the count — « de 3 » must
// not include one that will never happen — and an evaluation gets no ordinal
// at all: it is not a treatment.
export function workOrdinals(
  visits: Resource<'visit'>[],
): Map<string, { index: number; total: number }> {
  const live = visits
    // An EXTRA is standing work but OUTSIDE the program: no ordinal, never in
    // M — the mirror of the server's `extra: false`.
    .filter(v => isStandingWork(v) && v.attributes.extra !== true)
    .sort((a, b) => (a.attributes.position ?? 0) - (b.attributes.position ?? 0))
  return new Map(live.map((v, i) => [v.id, { index: i + 1, total: live.length }]))
}

// Standing work the plan-work flow can no longer DATE — the client mirror of
// Visit.dated_or_underway_work (jobs filter[undated_work] is its negation).
// Signature-seeding creates UNDATED standing work, so the job-page CTAs and
// the queue must gate on this, not hasStandingWork: a seeded program still
// needs its first date, and « Planifier les travaux » is how it gets one.
export function hasDatedOrUnderwayWork(visits: Resource<'visit'>[]): boolean {
  return visits.some(v =>
    v.attributes.kind !== 'evaluation'
    && v.attributes.status !== 'cancelled'
    && v.attributes.status !== 'skipped'
    && (v.attributes.scheduled_at != null
      || v.attributes.committed_on != null
      || v.attributes.status !== 'pending'))
}

export function nextVisit(visits: Resource<'visit'>[]): Resource<'visit'> | null {
  const open = visits.filter(v => {
    const status = v.attributes?.status
    return status != null && isVisitStatus(status) && !TERMINAL_VISIT_STATUSES.includes(status)
  })
  if (!open.length) return null

  return [...open].sort((a, b) => {
    const aAt = a.attributes?.scheduled_at
    const bAt = b.attributes?.scheduled_at
    if (aAt && bAt && aAt !== bAt) return aAt < bAt ? -1 : 1
    if (aAt && !bAt) return -1
    if (!aAt && bAt) return 1
    return (a.attributes?.position ?? 0) - (b.attributes?.position ?? 0)
  })[0]
}

// ── THE VISIT LADDER — which visit is next, and what it is owed ──────────────
// One derivation for every surface that offers a visit's forward verb: the job
// page's fixed CTA today, Home's « Ma journée » hero and /plan's stop card when
// they adopt it. Three copies of "which visit, and what verb" is how the day
// « Arrivé » is inserted in two places and forgotten in the third.

// The visit a surface should be talking about. A visit IN PROGRESS wins outright
// — you cannot be starting a second stop while standing in the first — and
// otherwise it is the next open one (nextVisit's order: dated first, then
// position). FieldDashboard spells exactly this inline today.
export function nextActionableVisit(visits: Resource<'visit'>[]): Resource<'visit'> | null {
  return visits.find(v => v.attributes?.status === 'working') ?? nextVisit(visits)
}

export type VisitVerbId = 'dispatch' | 'start_work' | 'complete'

export interface VisitVerb {
  /** The FSM event(s) to fire, in order. */
  events: VisitEventName[]
  labelKey: string
  icon: Component
  /** The button's identity for a surface that styles by target state. */
  id: VisitVerbId
}

// ⭐ `scheduled` fires TWO events. The state machine wants `dispatch` before
// `start_work`, but « répartir » is an OFFICE word: a crew standing in the
// driveway has one intention, and making them press two buttons to express it
// is the machine's bookkeeping leaking into the day. The office keeps `dispatch`
// as its own verb on the console — it is genuinely a separate act there.
// If the second call fails the visit rests at `dispatched`, a legal state, and
// the button simply reads « Démarrer » again.
const FIELD_LADDER: Partial<Record<VisitStatus, VisitVerb>> = {
  scheduled:  { events: ['dispatch', 'start_work'], labelKey: 'visits.actions.startWork', icon: Play,        id: 'start_work' },
  dispatched: { events: ['start_work'],             labelKey: 'visits.actions.startWork', icon: Play,        id: 'start_work' },
  working:    { events: ['complete'],               labelKey: 'visits.actions.complete',  icon: CheckCircle, id: 'complete' },
}

// The console's ladder: one event per rung, because the office IS the party that
// dispatches. Same table, different collapse — the difference is stated here
// rather than discovered in two templates.
const OFFICE_LADDER: Partial<Record<VisitStatus, VisitVerb>> = {
  scheduled:  { events: ['dispatch'],   labelKey: 'visits.actions.dispatch',  icon: Truck,       id: 'dispatch' },
  dispatched: { events: ['start_work'], labelKey: 'visits.actions.startWork', icon: Hammer,      id: 'start_work' },
  working:    { events: ['complete'],   labelKey: 'visits.actions.complete',  icon: CheckCircle, id: 'complete' },
}

export function nextVisitVerb(
  visit: Resource<'visit'> | null | undefined,
  surface: 'field' | 'office' = 'field',
): VisitVerb | null {
  const status = visit?.attributes?.status
  if (!status || !isVisitStatus(status)) return null
  return (surface === 'field' ? FIELD_LADDER : OFFICE_LADDER)[status] ?? null
}

// « Donner une date » — offered on a standing visit that carries NEITHER promise
// (no instant, no day); a dated row has nothing to give. A look-see earns it
// once the entente is taken — before that its first move is « On s'est
// entendu » — and its door is the evaluation's own (période or rendez-vous),
// never the work booking.
//
// Per-VISIT on purpose: as a job-level gate this act survived its own success
// (a bank placement leaves the visit undated), and a job with two undated
// treatments would silently date the first.
export function canGiveDate(visit: Resource<'visit'>): boolean {
  const a = visit.attributes
  return a.status === 'pending'
    && !a.scheduled_at
    && !a.committed_on
    && (a.kind !== 'evaluation' || a.confirmed === true)
}

// ── THE JOB PAGE's ONE ACT ───────────────────────────────────────────────────
// A dossier is a READING surface with ONE verb. The page's primary is the
// ladder's rung, and NOTHING repeats it: the row it names wears « suivante »
// instead of a second button, and its own kebab omits the act the masthead is
// already offering.
//
// Two species of rung, because a visit that cannot move on the FSM can still be
// owed a DATE — which is the commonest thing a banked job is waiting for, and
// exactly what the page used to bury in a per-row button among sixteen others.
export type JobLadderDoor = 'give_date'

export interface JobLadderAct {
  /** The visit the act NAMES — the row that wears « suivante ». */
  visit: Resource<'visit'>
  labelKey: string
  icon: Component
  /** An FSM rung: the surface fires `verb.events`, in order. */
  verb: VisitVerb | null
  /** A door the surface opens instead (a dialog on the desk, a drawer on the phone). */
  door: JobLadderDoor | null
}

// ⭐ ONE candidate, deliberately: the FRONT visit or nothing. It never scans on
// to a later visit that happens to have something owed — naming stop 3 while
// stop 1 waits is exactly the misdirection the ladder exists to prevent, and
// `start_work` stamps a crew, so a button pointing past the nearest visit
// rewrites an attribution. A silent ladder is a real answer: the surface falls
// back to the stage's verb, and where the front visit's own move lives
// elsewhere (an unconfirmed look-see's entente, on the phone's row) that is
// where the reader is meant to look.
export function jobVisitLadder(
  visits: Resource<'visit'>[],
  surface: 'field' | 'office' = 'field',
): JobLadderAct | null {
  const visit = nextActionableVisit(visits)
  if (!visit) return null

  const verb = nextVisitVerb(visit, surface)
  if (verb) return { visit, labelKey: verb.labelKey, icon: verb.icon, verb, door: null }

  if (canGiveDate(visit)) {
    return { visit, labelKey: 'visits.giveDate', icon: CalendarCheck, verb: null, door: 'give_date' }
  }
  return null
}

// ── Re-treatment window state ────────────────────────────────────────────────
// A visit's materialized window (due_from/due_to) is shown as one of:
//   firm      — anchored on the previous visit's ACTUAL completion (or manual)
//   projected — anchored on the previous visit's scheduled date (not yet firm)
//   overdue   — a non-terminal visit whose window has lapsed
//   none      — no window (visit 1, or nothing to anchor on)
export type WindowState = 'firm' | 'projected' | 'overdue' | 'none'

export interface WindowStateConfig {
  dotClass: string
  textClass: string
}

// Solid light + dark shade-pairs (no opacity) — the light-only version rendered
// as bright chips on the dark rail (unreadable). Dark keeps the same colour
// language, legible.
// Dot grammar (the UI-refresh mock, approved 2026-08-09): promise strength
// lives in the dot's FORM, not a pill — solid = firm, hollow ring = projected
// (the old dashed border's meaning, kept), red = overdue. Overdue is the only
// state whose text takes the hue: lateness colours the words.
export const WINDOW_STATE_CONFIG: Record<Exclude<WindowState, 'none'>, WindowStateConfig> = {
  firm:      { dotClass: 'bg-emerald-500 dark:bg-emerald-400', textClass: '' },
  projected: { dotClass: 'border-2 border-amber-500 bg-transparent dark:border-amber-400', textClass: '' },
  overdue:   { dotClass: 'bg-red-500 dark:bg-red-400', textClass: 'text-red-700 dark:text-red-400' },
}

// Derive the window state. `visits` (the job's siblings) lets us tell firm from
// projected by checking whether the PREVIOUS visit in the chain has completed.
// When siblings are absent (dashboard overdue list, planning-board stop cards),
// firm is never CLAIMED without evidence: a terminal visit's window is settled
// (firm), an open one degrades to projected.
export function windowState(visit: Resource<'visit'>, visits?: Resource<'visit'>[]): WindowState {
  const a = visit.attributes
  if (!a?.due_from || !a?.due_to) return 'none'

  const status = a.status
  const nonTerminal = status != null && isVisitStatus(status) && !TERMINAL_VISIT_STATUSES.includes(status)
  if (nonTerminal && isOverdue(a.due_to)) return 'overdue'

  if (a.window_overridden) return 'firm'

  if (visits?.length) {
    const prev = visits.find(v => (v.attributes?.position ?? 0) === (a.position ?? 0) - 1)
    return prev?.attributes?.status === 'completed' ? 'firm' : 'projected'
  }

  return nonTerminal ? 'projected' : 'firm'
}
